export type Accidental = 'sharp' | 'flat';
const sharpNotes = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'] as const;
const flatNotes = ['C', 'Db', 'D', 'Eb', 'E', 'F', 'Gb', 'G', 'Ab', 'A', 'Bb', 'B'] as const;
const aliases: Record<string, number> = {
  C: 0,
  'B#': 0,
  'C#': 1,
  Db: 1,
  D: 2,
  'D#': 3,
  Eb: 3,
  E: 4,
  Fb: 4,
  'E#': 5,
  F: 5,
  'F#': 6,
  Gb: 6,
  G: 7,
  'G#': 8,
  Ab: 8,
  A: 9,
  'A#': 10,
  Bb: 10,
  B: 11,
  Cb: 11,
};
const chordPattern =
  /^([A-G](?:#|b)?)((?:(?:m|maj|min|dim|aug|sus|add)?(?:2|4|5|6|7|9|11|13)?(?:[#b]\d+)*)?)(?:\/([A-G](?:#|b)?))?$/;

export function isValidKey(key: string): boolean {
  return aliases[key] !== undefined;
}
export function transposeNote(note: string, semitones: number, accidental: Accidental): string {
  const index = aliases[note];
  if (index === undefined) return note;
  const notes = accidental === 'flat' ? flatNotes : sharpNotes;
  return notes[(((index + semitones) % 12) + 12) % 12] ?? note;
}
export function transposeChord(chord: string, semitones: number, accidental: Accidental): string {
  const match = chord.match(chordPattern);
  if (!match?.[1]) return chord;
  const root = transposeNote(match[1], semitones, accidental);
  const quality = match[2] ?? '';
  const bass = match[3] ? `/${transposeNote(match[3], semitones, accidental)}` : '';
  return `${root}${quality}${bass}`;
}
export function transposeContent(
  content: string,
  semitones: number,
  accidental: Accidental,
): string {
  return content.replace(/\[([^\]\n]+)\]/g, (whole, token: string) =>
    chordPattern.test(token) ? `[${transposeChord(token, semitones, accidental)}]` : whole,
  );
}

const chordTokens = (line: string): { chord: string; index: number }[] | null => {
  const matches = [...line.matchAll(/\[[^\]\n]+\]|\S+/g)];
  const tokens = matches.map((match) => {
    const raw = match[0];
    const chord = raw.startsWith('[') && raw.endsWith(']') ? raw.slice(1, -1) : raw;
    return { chord, index: match.index ?? 0 };
  });
  if (!tokens.length || tokens.some(({ chord }) => !chordPattern.test(chord))) return null;
  return tokens;
};

export function alignedChordsToChordPro(content: string): string {
  const lines = content.replace(/\r\n/g, '\n').split('\n');
  const converted: string[] = [];
  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index] ?? '';
    const chords = chordTokens(line);
    if (!chords) {
      converted.push(line);
      continue;
    }
    const lyric = lines[index + 1];
    if (
      lyric === undefined ||
      lyric.trim() === '' ||
      /^\s*\[[^\]]+\]\s*$/.test(lyric) ||
      chordTokens(lyric)
    ) {
      converted.push(
        line.replace(/\[[^\]\n]+\]|\S+/g, (token) =>
          token.startsWith('[') && token.endsWith(']') ? token : `[${token}]`,
        ),
      );
      continue;
    }
    let cursor = 0;
    let chordPro = '';
    for (const { chord, index: chordIndex } of chords) {
      const insertionPoint = Math.min(Math.max(chordIndex, cursor), lyric.length);
      chordPro += `${lyric.slice(cursor, insertionPoint)}[${chord}]`;
      cursor = insertionPoint;
    }
    chordPro += lyric.slice(cursor);
    converted.push(chordPro);
    index += 1;
  }
  return converted.join('\n');
}
export type ChordLine =
  | { type: 'section'; text: string }
  | { type: 'line'; segments: { chord?: string; lyric: string }[] };
export function parseChordPro(content: string): ChordLine[] {
  return content.split('\n').map((line) => {
    if (/^\[[^\]]+\]$/.test(line) && !chordPattern.test(line.slice(1, -1)))
      return { type: 'section', text: line.slice(1, -1) };
    const segments: { chord?: string; lyric: string }[] = [];
    const regex = /\[([^\]]+)\]/g;
    let chord: string | undefined;
    let index = 0;
    for (const match of line.matchAll(regex)) {
      if (match.index! > index || chord)
        segments.push({ chord, lyric: line.slice(index, match.index) });
      chord = chordPattern.test(match[1] ?? '') ? match[1] : undefined;
      if (!chord) segments.push({ lyric: match[0] });
      index = match.index! + match[0].length;
    }
    segments.push({ chord, lyric: line.slice(index) });
    return { type: 'line', segments };
  });
}
